/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import getConfig from 'next/config';
import { CSSProperties, Suspense } from 'react';

import styles from './LinkPreview.module.less';

type Preview = {
  title: string;
  description: string;
  img: string;
  domain: string;
} | null;

// Cached responses or running request promises
const PREVIEW_CACHE: Record<string, Promise<Preview> | { preview: Preview }> =
  {};

const URL_MATCHER =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

function useSuspenseRequest(url: string) {
  let cached = PREVIEW_CACHE[url];

  if (!url.match(URL_MATCHER)) {
    return { preview: null };
  }

  if (!cached) {
    const { publicRuntimeConfig } = getConfig() || {};
    const basePath =
      (publicRuntimeConfig && publicRuntimeConfig.staticFolder) || '';
    const apiUrl = `${basePath}/api/link-preview?url=${encodeURI(url)}`;

    cached = PREVIEW_CACHE[url] = fetch(apiUrl)
      .then((response) => response.json())
      .then((preview) => {
        PREVIEW_CACHE[url] = preview;
        return preview;
      })
      .catch(() => {
        PREVIEW_CACHE[url] = { preview: null };
      });
  }

  if (cached instanceof Promise) {
    throw cached;
  }

  return cached;
}

function LinkPreviewContent({
  url,
}: Readonly<{
  url: string;
}>): JSX.Element | null {
  const { preview } = useSuspenseRequest(url);
  if (preview === null) {
    return null;
  }
  return (
    <a
      className={styles['LinkPreview__container']}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {preview.img && (
        <div className={styles['LinkPreview__imageWrapper']}>
          <img
            src={preview.img}
            alt={preview.title}
            className={styles['LinkPreview__image']}
          />
        </div>
      )}
      <div className={styles['LinkPreview__details']}>
        {preview.domain && (
          <div className={styles['LinkPreview__domainRow']}>
            <span className={styles['LinkPreview__favicon']} />
            <span className={styles['LinkPreview__domain']}>
              {preview.domain}
            </span>
          </div>
        )}
        {preview.title && (
          <div className={styles['LinkPreview__title']}>{preview.title}</div>
        )}
        {preview.description && (
          <div className={styles['LinkPreview__description']}>
            {preview.description}
          </div>
        )}
      </div>
    </a>
  );
}

function Glimmer(props: { style: CSSProperties; index: number }): JSX.Element {
  return (
    <div
      className={styles['LinkPreview__glimmer']}
      {...props}
      style={{
        animationDelay: String((props.index || 0) * 300),
        ...(props.style || {}),
      }}
    />
  );
}

export default function LinkPreview({
  url,
}: Readonly<{
  url: string;
}>): JSX.Element {
  return (
    <Suspense
      fallback={
        <div className="bg-white border border-gray-300 rounded-xl p-4 mb-4">
          <Glimmer style={{ height: '80px' }} index={0} />
          <Glimmer style={{ width: '60%' }} index={1} />
          <Glimmer style={{ width: '80%' }} index={2} />
        </div>
      }
    >
      <LinkPreviewContent url={url} />
    </Suspense>
  );
}
